
import { supabase } from "@/integrations/supabase/client";
import { ShoppingList } from "@/types";
import { mapItemFromDatabase, mapListFromDatabase } from "./utils";
import { networkService } from '@/services/network.service';
import { syncService } from '@/services/sync.service';
import { offlineDb } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';

/**
 * Update rapid_insert flag with offline support
 */
export const updateRapidInsertFlag = async (listId: string, rapid: boolean) => {
  try {
    const isOnline = networkService.isOnline();

    if (isOnline) {
      try {
        const { error } = await supabase
          .from("shopping_lists")
          .update({ rapid_insert: rapid })
          .eq("id", listId);

        if (error) {
          OfflineLogger.error("updateRapidInsertFlag", `Errore aggiornando rapid_insert: ${error.message}`);
          return false;
        } else {
          OfflineLogger.info("updateRapidInsertFlag", `🟢 rapid_insert aggiornato a ${rapid}`);
          return true;
        }
      } catch (err: any) {
        if (err?.message?.includes("Failed to fetch")) {
          try {
            await offlineDb.shoppingLists.update(listId, {
              rapid_insert: rapid,
              _syncStatus: 'pending',
              _lastModified: Date.now()
            });

            await syncService.addToSyncQueue(
              "shoppingLists",
              "update",
              listId,
              { rapid_insert: rapid }
            );

            OfflineLogger.info("updateRapidInsertFlag", `🟠 rapid_insert aggiornato offline a ${rapid} (in attesa di sincronizzazione)`);
            return true;
          } catch (offlineErr) {
            OfflineLogger.error("updateRapidInsertFlag", `Errore aggiornando rapid_insert offline: ${offlineErr}`);
            return false;
          }
        }

        OfflineLogger.error("updateRapidInsertFlag", `Errore aggiornando rapid_insert: ${err}`);
        return false;
      }
    } else {
      // Offline version
      try {
        // Update local DB
        await offlineDb.shoppingLists.update(listId, {
          rapid_insert: rapid,
          _syncStatus: 'pending',
          _lastModified: Date.now()
        });
        
        // Add to sync queue
        await syncService.addToSyncQueue(
          "shoppingLists", 
          "update", 
          listId, 
          { rapid_insert: rapid }
        );
        
        OfflineLogger.info("updateRapidInsertFlag", `🟠 rapid_insert aggiornato offline a ${rapid} (in attesa di sincronizzazione)`);
        return true;
      } catch (err) {
        OfflineLogger.error("updateRapidInsertFlag", `Errore aggiornando rapid_insert offline: ${err}`);
        return false;
      }
    }
  } catch (err) {
    OfflineLogger.error("updateRapidInsertFlag", `Errore generico: ${err}`);
    return false;
  }
};

/**
 * Update list background with offline support
 */
export const updateListBackground = async (listId: string, backgroundGradient: string): Promise<boolean> => {
  try {
    const isOnline = networkService.isOnline();

    if (isOnline) {
      try {
        const { error } = await supabase
          .from('shopping_lists')
          .update({
            background_gradient: backgroundGradient,
            updated_at: new Date().toISOString()
          })
          .eq('id', listId);

        if (error) {
          OfflineLogger.error(`updateListBackground`, `Error updating list background ${listId}: ${error.message}`);
          return false;
        }

        return true;
      } catch (err: any) {
        if (err?.message?.includes('Failed to fetch')) {
          try {
            await offlineDb.shoppingLists.update(listId, {
              background_gradient: backgroundGradient,
              updated_at: new Date().toISOString(),
              _syncStatus: 'pending',
              _lastModified: Date.now()
            });

            await syncService.addToSyncQueue(
              'shoppingLists',
              'update',
              listId,
              {
                background_gradient: backgroundGradient,
                updated_at: new Date().toISOString()
              }
            );

            OfflineLogger.info('updateListBackground', `🟠 background_gradient aggiornato offline per lista ${listId} (in attesa di sincronizzazione)`);
            return true;
          } catch (offlineErr) {
            OfflineLogger.error('updateListBackground', `Errore aggiornando background_gradient offline: ${offlineErr}`);
            return false;
          }
        }

        OfflineLogger.error('updateListBackground', `Errore aggiornando background_gradient: ${err}`);
        return false;
      }
    } else {
      // Offline version
      try {
        // Update local DB
        await offlineDb.shoppingLists.update(listId, {
          background_gradient: backgroundGradient,
          updated_at: new Date().toISOString(),
          _syncStatus: 'pending',
          _lastModified: Date.now()
        });
        
        // Add to sync queue
        await syncService.addToSyncQueue(
          "shoppingLists", 
          "update", 
          listId, 
          { 
            background_gradient: backgroundGradient,
            updated_at: new Date().toISOString()
          }
        );
        
        OfflineLogger.info("updateListBackground", `🟠 background_gradient aggiornato offline per lista ${listId} (in attesa di sincronizzazione)`);
        return true;
      } catch (err) {
        OfflineLogger.error("updateListBackground", `Errore aggiornando background_gradient offline: ${err}`);
        return false;
      }
    }
  } catch (err) {
    OfflineLogger.error(`updateListBackground`, `General error updating list background ${listId}: ${err}`);
    return false;
  }
};

/**
 * Rename a list with offline support
 */
export const renameShoppingList = async (listId: string, name: string): Promise<boolean> => {
  try {
    const isOnline = networkService.isOnline();

    if (isOnline) {
      try {
        const { error } = await supabase
          .from('shopping_lists')
          .update({ name, updated_at: new Date().toISOString() })
          .eq('id', listId);

        if (error) {
          OfflineLogger.error('renameShoppingList', `Error renaming list ${listId}: ${error.message}`);
          return false;
        }

        return true;
      } catch (err: any) {
        if (err?.message?.includes('Failed to fetch')) {
          try {
            await offlineDb.shoppingLists.update(listId, {
              name,
              updated_at: new Date().toISOString(),
              _syncStatus: 'pending',
              _lastModified: Date.now()
            });

            await syncService.addToSyncQueue(
              'shoppingLists',
              'update',
              listId,
              { name, updated_at: new Date().toISOString() }
            );

            OfflineLogger.info('renameShoppingList', `🟠 name updated offline for list ${listId} (pending sync)`);
            return true;
          } catch (offlineErr) {
            OfflineLogger.error('renameShoppingList', `Errore aggiornando nome offline: ${offlineErr}`);
            return false;
          }
        }

        OfflineLogger.error('renameShoppingList', `Error renaming list ${listId}: ${err}`);
        return false;
      }
    } else {
      try {
        await offlineDb.shoppingLists.update(listId, {
          name,
          updated_at: new Date().toISOString(),
          _syncStatus: 'pending',
          _lastModified: Date.now()
        });

        await syncService.addToSyncQueue(
          'shoppingLists',
          'update',
          listId,
          { name, updated_at: new Date().toISOString() }
        );

        OfflineLogger.info('renameShoppingList', `🟠 name updated offline for list ${listId} (pending sync)`);
        return true;
      } catch (err) {
        OfflineLogger.error('renameShoppingList', `Errore aggiornando nome offline: ${err}`);
        return false;
      }
    }
  } catch (err) {
    OfflineLogger.error('renameShoppingList', `General error renaming list ${listId}: ${err}`);
    return false;
  }
};

